import { ICD_VERSION } from 'src/config-global';

import { icdGet } from './icd-client';

// ----------------------------------------------------------------------
// Autodetección de la release CIE-11 cargada en el contenedor.
//
// El `releaseId` es obligatorio en toda ruta de búsqueda y detalle, y fijarlo
// en el .env ya salió caro: una release inexistente responde 404 en texto
// plano y el buscador queda mudo, sin error visible. El .env pasa a ser solo
// respaldo.
//
// Mismo patrón que insurance-catalog-cache.js: se memoiza LA PROMESA (dos
// llamadas simultáneas comparten una única petición), espejo en
// sessionStorage, y NUNCA se memoiza un rechazo.
// ----------------------------------------------------------------------

const CLAVE_SESION = 'medicore.icd.release';
const TTL_MS = 6 * 60 * 60 * 1000;
const RUTA_RELEASES = '/icd/release/11/mms';

let promesaRelease = null;

const FORMA_RELEASE = /^\d{4}-\d{2}$/;

const leerSesion = () => {
  try {
    const crudo = sessionStorage.getItem(CLAVE_SESION);
    if (!crudo) return null;
    const guardado = JSON.parse(crudo);
    if (Date.now() - guardado.cachedAt > TTL_MS) return null;
    // Se revalida la forma: una versión basura cacheada dejaría el buscador
    // devolviendo 404 durante toda la pestaña, sin forma de recuperarse.
    return FORMA_RELEASE.test(guardado.release) ? guardado.release : null;
  } catch {
    // Una caché ilegible se trata como si no existiera y se vuelve a detectar.
    return null;
  }
};

const escribirSesion = (release) => {
  try {
    sessionStorage.setItem(CLAVE_SESION, JSON.stringify({ release, cachedAt: Date.now() }));
  } catch {
    // sessionStorage lleno o bloqueado: la memoización en memoria sigue sirviendo.
  }
};

// `latestRelease` es la URI COMPLETA de la linealización
// (http://id.who.int/icd/release/11/2025-01/mms), así que su último segmento es
// "mms", no la release: el id va justo detrás de `/release/{n}/`. Se valida la
// forma AAAA-MM porque un id inventado produce un 404 mudo en cada búsqueda.
const releaseDesdeUri = (uri) => {
  const encontrado = String(uri ?? '').match(/\/release\/\d+\/(\d{4}-\d{2})(?:[/?#]|$)/i);
  return encontrado?.[1] ?? '';
};

const releaseDeLaRespuesta = (datos) =>
  releaseDesdeUri(datos?.latestRelease) ||
  releaseDesdeUri(Array.isArray(datos?.release) ? datos.release[0] : '');

/**
 * Release cargada en el servicio, p. ej. '2025-01'. Cae al respaldo
 * ICD_VERSION del .env si la detección falla, y en ese caso no la cachea:
 * el siguiente intento vuelve a detectar.
 * @returns {Promise<string>}
 */
export async function obtenerRelease() {
  const enSesion = leerSesion();
  if (enSesion) return enSesion;

  if (!promesaRelease) {
    promesaRelease = icdGet(RUTA_RELEASES)
      .then((datos) => {
        const release = releaseDeLaRespuesta(datos);
        if (!release) {
          promesaRelease = null;
          console.warn(
            `[CIE-11] La respuesta de releases no trae ninguna versión reconocible; se usa el respaldo ${ICD_VERSION}.`
          );
          return ICD_VERSION;
        }

        escribirSesion(release);
        return release;
      })
      .catch((error) => {
        // Un rechazo memoizado dejaría el buscador muerto toda la sesión.
        promesaRelease = null;
        console.warn(
          `[CIE-11] No se pudo autodetectar la release; se usa el respaldo ${ICD_VERSION}.`,
          error?.message ?? error
        );
        return ICD_VERSION;
      });
  }

  return promesaRelease;
}

/**
 * Prefijo de todas las rutas MMS de una release.
 * @param {string} release
 * @returns {string}
 */
export function rutaMms(release) {
  return `/icd/release/11/${release}/mms`;
}

/** Fuerza una nueva detección en la próxima llamada. */
export function invalidarRelease() {
  promesaRelease = null;
  try {
    sessionStorage.removeItem(CLAVE_SESION);
  } catch {
    // Sin acceso a sessionStorage basta con haber limpiado la memoria.
  }
}
